// src/components/find-cars/find-cars.ts
import { toastError, toastSuccess } from "../toast/toast";
import { saveCar, isCarSaved, displaySavedCars, imageUrlToDataUrl } from "../saved-cars/saved-cars";
import { setupImageFallbacks, isBlockedImageUrl } from "../../utils/image-proxy";
import {
  buildAmazonSearchUrl,
  carLabelFromQuery,
  AFFILIATE_DISCLOSURE,
  AFFILIATE_LINKS_ENABLED,
} from "../../utils/amazon-affiliate";

// Config
const LOADING_TEST_MODE = false;

interface CarsXEImage {
  link: string;
  thumbnailLink?: string;
}

// Validation helper for search query
function validateQuery(value: string): string | null {
  if (value.length < 5) {
    return "Search query must be at least 5 characters";
  }
  if (value.length > 200) {
    return "Search query is too long (max 200 characters)";
  }
  return null;
}

interface CarsXEResponse {
  images?: CarsXEImage[];
  success: boolean;
  error?: string;
  query: Record<string, string>;
}

const CAR_ICON_SVG = `<svg aria-hidden="true"><use href="/sprite.svg#icon-directions-car"></use></svg>`;

// Above this length, "Find <car> models" overflows the button on small
// screens, so fall back to a static label.
const MAX_BUTTON_LABEL_LENGTH = 18;

/** Remove any existing affiliate CTA card from above the results grid. */
function clearAffiliateCta(): void {
  document.getElementById("affiliate-cta")?.remove();
}

/**
 * Render a contextual Amazon affiliate CTA above the results grid, pointing at
 * a model-car search for the current query. Built with DOM APIs (not innerHTML)
 * so the query can't inject markup; the URL is encoded by buildAmazonSearchUrl.
 */
function renderAffiliateCta(query: string, resultsContainer: Element): void {
  clearAffiliateCta();
  if (!AFFILIATE_LINKS_ENABLED) return;

  const label = carLabelFromQuery(query);
  const cta = document.createElement("aside");
  cta.id = "affiliate-cta";
  cta.className = "affiliate-cta";

  const icon = document.createElement("span");
  icon.className = "affiliate-cta__icon";
  icon.innerHTML = CAR_ICON_SVG;

  const text = document.createElement("p");
  text.className = "affiliate-cta__text";
  const strong = document.createElement("strong");
  strong.textContent = label;
  const article = /^[aeiou]/i.test(label) ? "an" : "a";
  text.append(`Can't park ${article} `, strong, " in your driveway yet? Park one on your desk.");

  const link = document.createElement("a");
  link.className = "affiliate-cta__link";
  link.href = buildAmazonSearchUrl(query);
  link.target = "_blank";
  link.rel = "sponsored noopener";
  link.textContent =
    label.length <= MAX_BUTTON_LABEL_LENGTH ? `Find ${label} models` : "Find yours on Amazon";

  const disclosure = document.createElement("p");
  disclosure.className = "affiliate-cta__disclosure";
  disclosure.textContent = AFFILIATE_DISCLOSURE;

  cta.append(icon, text, link, disclosure);
  // Place below the results grid — users browse the images first, then hit the
  // "I love this car" moment at the bottom, which is when the CTA converts best.
  resultsContainer.parentNode?.insertBefore(cta, resultsContainer.nextSibling);
}

export function initializeFindCars(): void {
  const form = document.getElementById("find-cars-form") as HTMLFormElement;
  const resultsContainer = document.getElementById("find-cars-results");

  // Set up event delegation for save buttons
  if (resultsContainer) {
    resultsContainer.addEventListener("click", async (e) => {
      const target = e.target as HTMLElement;
      const saveButton = target.closest(".save-button") as HTMLButtonElement;

      if (saveButton && !saveButton.disabled) {
        const li = saveButton.closest("li");
        if (!li) return;

        const img = li.querySelector("img") as HTMLImageElement;
        if (!img) return;

        // Get original URL from data attribute (before any proxy fallback)
        const imageUrl = img.dataset.originalUrl || img.src;
        const searchParams = {
          query: img.dataset.query || "",
        };

        // Disable button and show loading state
        saveButton.disabled = true;
        saveButton.classList.add("loading");

        try {
          // Convert image to data URL
          const imageData = await imageUrlToDataUrl(imageUrl, searchParams.query);

          saveCar(imageUrl, imageData, searchParams);
          toastSuccess("Car saved to garage!");

          // Update button state
          saveButton.classList.remove("loading");
          saveButton.classList.add("saved");
          saveButton.removeAttribute("data-tooltip");

          // Change icon to bookmark-added
          const svg = saveButton.querySelector("svg use");
          if (svg) {
            svg.setAttribute("href", "/sprite.svg#icon-bookmark-added");
          }

          // Update image state
          img.classList.add("saved");

          // Refresh garage display
          displaySavedCars();
        } catch (error) {
          // Re-enable button on failure
          saveButton.disabled = false;
          saveButton.classList.remove("loading");

          if (error instanceof Error) {
            toastError(error.message);
          } else {
            toastError("Failed to save car");
          }
        }
      }
    });
  }

  // Handle search suggestion clicks
  const searchSuggestions = document.querySelectorAll<HTMLInputElement>('input[name="search-suggestion"]');
  const searchInput = document.getElementById("car-search-input") as HTMLInputElement;

  searchSuggestions.forEach((radio) => {
    radio.addEventListener("change", () => {
      if (searchInput && radio.value) {
        searchInput.value = radio.value;
      }
    });
  });

  // Prefill the search from a ?q= URL param (links from the tool pages), but
  // don't auto-submit — each search costs an API call, so the user presses Search.
  const urlQuery = new URLSearchParams(window.location.search).get("q");
  if (searchInput && urlQuery) {
    searchInput.value = urlQuery;
    searchInput.focus({ preventScroll: true });
    searchInput.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  if (form) {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (!submitButton) return;

      try {
        // Add loading state to button
        submitButton.classList.add("loading");
        submitButton.disabled = true;

        const queryInput = document.getElementById("car-search-input") as HTMLInputElement;
        const query = queryInput.value.trim();

        // Validate query
        const queryError = validateQuery(query);
        if (queryError) {
          toastError(queryError);
          return;
        }

        const params: Record<string, string> = { q: query };

        if (!resultsContainer) return;

        resultsContainer.innerHTML = "";
        clearAffiliateCta();

        let data: CarsXEResponse;

        if (LOADING_TEST_MODE) {
          // Test mode: wait 4 seconds then show mock data
          await new Promise((resolve) => setTimeout(resolve, 4000));
          data = {
            success: true,
            query: params,
            images: Array.from({ length: 10 }, (_, i) => ({
              link: `https://picsum.photos/600/400?random=${i}`,
              thumbnailLink: `https://picsum.photos/300/200?random=${i}`,
            })),
          };
        } else {
          // Normal mode: call the API
          const queryParams = new URLSearchParams(params);
          const response = await fetch(`/.netlify/functions/get-images?${queryParams}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch images: ${response.status}`);
          }
          const cacheStatus = response.headers.get("X-Cache");
          console.log(`[Image Search] Query: "${params.q}" - Cache: ${cacheStatus || "UNKNOWN"}`);
          data = await response.json();
        }

        if (data.images && data.images.length > 0) {
          data.images.forEach((image) => {
            // Skip images from domains that block external requests
            if (isBlockedImageUrl(image.link)) return;

            const li = document.createElement("li");
            const alreadySaved = isCarSaved(image.link);

            li.innerHTML = `
              <img
                src="${image.link}"
                alt="Car image"
                loading="lazy"
                data-original-url="${image.link}"
                data-query="${params.q}"
              />
              <div class="save-button-wrapper">
                <button
                  class="save-button ${alreadySaved ? "saved" : ""}"
                  ${!alreadySaved ? 'data-tooltip="Save to garage"' : ""}
                  ${alreadySaved ? "disabled" : ""}
                >
                  <svg><use href="/sprite.svg#icon-${alreadySaved ? "bookmark-added" : "add"}"></use></svg>
                </button>
              </div>
            `;
            resultsContainer.appendChild(li);
          });

          // Set up fallback to proxy for images that fail to load directly
          setupImageFallbacks(resultsContainer);

          // Contextual Amazon affiliate CTA for the searched car
          renderAffiliateCta(query, resultsContainer);
        } else {
          resultsContainer.innerHTML = "<li>No images found</li>";
        }
      } catch (error) {
        console.error("Error fetching car images:", error);
        toastError("Error loading images. Please try again.");
      } finally {
        // Remove loading state from button
        submitButton.classList.remove("loading");
        submitButton.disabled = false;
      }
    });
  }
}
